import {
    ReloadOutlined,
    TagOutlined,
    SearchOutlined,
    CalendarOutlined,
    PercentageOutlined,
    DollarOutlined,
    DownOutlined,
    RightOutlined,
    ShopOutlined,
    UserOutlined,
    BankOutlined
} from "@ant-design/icons";
import {
    Button,
    Card,
    Col,
    Empty,
    Row,
    Space,
    Spin,
    Table,
    Tooltip,
    Typography,
    Input,
    Tag,
    Select,
    Badge,
    Divider,
    Statistic,
    Collapse,
    List
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { getAllActiveCoupons, searchCoupons, getCouponsByStatus } from "../../services/couponService";
import { syncCoupons, shouldSyncCoupons } from "../../services/couponSyncService";
import "./CouponsStyles.css";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const CouponsList = () => {
    const [couponsData, setCouponsData] = useState([]);
    const [allCouponsData, setAllCouponsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const isMobile = window.innerWidth < 768;


    const loadCoupons = useCallback(async () => {
        try {
            console.log("Cargando cupones activos...");
            setLoading(true);

            // Verificar si necesitamos sincronizar
            const needsSync = await shouldSyncCoupons();
            if (needsSync) {
                console.log("Sincronizando cupones desde Business Central...");
                await syncCoupons();
            }

            // Obtener cupones desde la base de datos local
            const data = await getAllActiveCoupons();
            setAllCouponsData(data);
            setCouponsData(data);
        } catch (error) {
            window.messageApi.error("Error al cargar cupones activos: " + error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Cargar cupones al inicio
    useEffect(() => {
        loadCoupons();
    }, [loadCoupons]);

    // Función para forzar actualización desde el servidor
    const forceRefresh = useCallback(async () => {
        try {
            setLoading(true);
            console.log("Forzando actualización desde Business Central...");

            // Forzar sincronización
            const syncResult = await syncCoupons();
            if (syncResult) {
                // Obtener cupones actualizados
                const data = await getAllActiveCoupons();
                setAllCouponsData(data);
                setCouponsData(data);
                window.messageApi.success("Cupones actualizados correctamente");
            } else {
                window.messageApi.warning("No se pudo conectar al servidor, mostrando datos locales");
                // Intentar obtener datos locales
                const localData = await getAllActiveCoupons();
                setAllCouponsData(localData);
                setCouponsData(localData);
            }
        } catch (error) {
            window.messageApi.error("Error al actualizar cupones: " + error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Función para manejar la búsqueda
    const handleSearch = async (value) => {
        setSearchText(value);
        try {
            setLoading(true);
            let filteredData = allCouponsData;

            if (value) {
                filteredData = await searchCoupons(value);
            }

            // Aplicar filtro de estado si está activo
            if (statusFilter !== "all") {
                filteredData = filteredData.filter(coupon => coupon.Status === statusFilter);
            }

            setCouponsData(filteredData);
        } catch (error) {
            window.messageApi.error("Error al buscar cupones: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Función para manejar el filtro por estado
    const handleStatusFilter = async (status) => {
        setStatusFilter(status);
        try {
            setLoading(true);
            let filteredData = allCouponsData;

            if (status !== "all") {
                filteredData = await getCouponsByStatus(status);
            }

            // Aplicar búsqueda si hay texto
            if (searchText) {
                filteredData = filteredData.filter(coupon =>
                    coupon.Description?.toLowerCase().includes(searchText.toLowerCase()) ||
                    coupon.No?.toLowerCase().includes(searchText.toLowerCase())
                );
            }

            setCouponsData(filteredData);
        } catch (error) {
            window.messageApi.error("Error al filtrar cupones: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Función para obtener color del estado
    const getStatusColor = (status) => {
        switch (status) {
            case "Pendiente":
                return "orange";
            case "Utilizado":
                return "green";
            case "Caducado":
                return "red";
            case "Cancelado":
                return "gray";
            default:
                return "blue";
        }
    };

    // Función para obtener icono según el tipo de detalle
    const getDetailIcon = (detailType) => {
        switch (detailType) {
            case "TPV":
                return <ShopOutlined style={{ color: "#1890ff" }} />;
            case "Cliente":
                return <UserOutlined style={{ color: "#52c41a" }} />;
            case "Colegio":
                return <BankOutlined style={{ color: "#722ed1" }} />;
            default:
                return <TagOutlined style={{ color: "#666" }} />;
        }
    };

    // Función para formatear fechas
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        try {
            // El formato viene como DD/MM/YY, necesitamos convertirlo
            const parts = dateString.split('/');
            if (parts.length === 3) {
                const day = parts[0];
                const month = parts[1];
                let year = parts[2];

                // Si el año es de 2 dígitos, convertir a 4 dígitos
                if (year.length === 2) {
                    // Asumimos que años 00-30 son 2000-2030, y 31-99 son 1931-1999
                    const yearNum = parseInt(year);
                    year = yearNum <= 30 ? `20${year}` : `19${year}`;
                }

                // Crear fecha en formato YYYY-MM-DD para que Date() la interprete correctamente
                const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                const date = new Date(isoDate);

                // Verificar que la fecha es válida
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString('es-ES');
                }
            }

            // Si no se puede parsear, devolver el string original
            return dateString;
        } catch {
            return dateString;
        }
    };

    // Función para formatear montos
    const formatAmount = (amount) => {
        if (!amount || amount === 0) return "-";
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    // Componente para mostrar los detalles expandidos
    const expandedRowRender = (record) => {
        const { Coupon_Details, Sales_Prices, Line_Discounts } = record;

        return (
            <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
                <Collapse defaultActiveKey={['details']} ghost>
                    {/* Detalles del Cupón */}
                    {Coupon_Details && Coupon_Details.length > 0 && (
                        <Panel
                            header={
                                <Space>
                                    <TagOutlined />
                                    <strong>Detalles del Cupón ({Coupon_Details.length})</strong>
                                </Space>
                            }
                            key="details"
                        >
                            <List
                                dataSource={Coupon_Details}
                                renderItem={(detail, index) => (
                                    <List.Item key={index}>
                                        <List.Item.Meta
                                            avatar={getDetailIcon(detail.Detail_Type)}
                                            title={
                                                <Space>
                                                    <Text strong>{detail.Detail_Type}</Text>
                                                    <Text code>{detail.No}</Text>
                                                    <Tag color={getStatusColor(detail.Status)}>
                                                        {detail.Status}
                                                    </Tag>
                                                </Space>
                                            }
                                            description={
                                                <Space size="large">
                                                    {detail.Discount_Percentage > 0 && (
                                                        <Text>
                                                            <PercentageOutlined style={{ color: "#52c41a" }} />
                                                            {" "}{detail.Discount_Percentage}%
                                                        </Text>
                                                    )}
                                                    {detail.Discount_Amount > 0 && (
                                                        <Text>
                                                            <DollarOutlined style={{ color: "#52c41a" }} />
                                                            {" "}{formatAmount(detail.Discount_Amount)}
                                                        </Text>
                                                    )}
                                                    <Text>
                                                        Total: {formatAmount(detail.Total_Discounted_Amount)}
                                                    </Text>
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        </Panel>
                    )}

                    {/* Precios de Venta */}
                    {Sales_Prices && Sales_Prices.length > 0 && (
                        <Panel
                            header={
                                <Space>
                                    <DollarOutlined />
                                    <strong>Precios de Venta ({Sales_Prices.length})</strong>
                                </Space>
                            }
                            key="prices"
                        >
                            <List
                                dataSource={Sales_Prices}
                                renderItem={(price, index) => (
                                    <List.Item key={index}>
                                        <List.Item.Meta
                                            title={
                                                <Space>
                                                    <Text strong>Producto: {price.Item_No}</Text>
                                                    <Text>Precio: {formatAmount(price.Unit_Price)}</Text>
                                                </Space>
                                            }
                                            description={
                                                <Space>
                                                    <Text>Desde: {formatDate(price.Starting_Date)}</Text>
                                                    <Text>Hasta: {formatDate(price.Ending_Date)}</Text>
                                                    {price.Minimum_Quantity > 0 && (
                                                        <Text>Cantidad mín: {price.Minimum_Quantity}</Text>
                                                    )}
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        </Panel>
                    )}

                    {/* Descuentos por Línea */}
                    {Line_Discounts && Line_Discounts.length > 0 && (
                        <Panel
                            header={
                                <Space>
                                    <PercentageOutlined />
                                    <strong>Descuentos por Línea ({Line_Discounts.length})</strong>
                                </Space>
                            }
                            key="discounts"
                        >
                            <List
                                dataSource={Line_Discounts}
                                renderItem={(discount, index) => (
                                    <List.Item key={index}>
                                        <List.Item.Meta
                                            title={
                                                <Space>
                                                    <Text strong>{discount.Type}: {discount.Code}</Text>
                                                    <Text>{discount.Line_Discount_Percentage}% descuento</Text>
                                                </Space>
                                            }
                                            description={
                                                <Space>
                                                    <Text>Desde: {formatDate(discount.Starting_Date)}</Text>
                                                    <Text>Hasta: {formatDate(discount.Ending_Date)}</Text>
                                                    {discount.Minimum_Quantity > 0 && (
                                                        <Text>Cantidad mín: {discount.Minimum_Quantity}</Text>
                                                    )}
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        </Panel>
                    )}
                </Collapse>
            </div>
        );
    };

    const columns = [
        {
            title: "",
            key: "expand",
            width: 50,
            render: (_, record) => {
                const hasDetails = (record.Coupon_Details && record.Coupon_Details.length > 0) ||
                    (record.Sales_Prices && record.Sales_Prices.length > 0) ||
                    (record.Line_Discounts && record.Line_Discounts.length > 0);

                if (!hasDetails) return null;

                const isExpanded = expandedRowKeys.includes(record.No);
                return (
                    <Button
                        type="text"
                        size="small"
                        icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                        onClick={() => {
                            const newExpandedKeys = isExpanded
                                ? expandedRowKeys.filter(key => key !== record.No)
                                : [...expandedRowKeys, record.No];
                            setExpandedRowKeys(newExpandedKeys);
                        }}
                    />
                );
            },
        },
        {
            title: "Número",
            dataIndex: "No",
            key: "No",
            width: 120,
            fixed: isMobile ? false : "left",
            render: (text) => (
                <Space>
                    <TagOutlined style={{ color: "#1890ff" }} />
                    <strong>{text || "-"}</strong>
                </Space>
            ),
        },
        {
            title: "Descripción",
            dataIndex: "Description",
            key: "Description",
            width: 200,
            render: (text) => (
                <span style={{ fontWeight: 500 }}>{text || "-"}</span>
            ),
        },
        {
            title: "Estado",
            dataIndex: "Status",
            key: "Status",
            width: 120,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status || "Sin estado"}
                </Tag>
            ),
        },
        {
            title: "Activo",
            dataIndex: "Active",
            key: "Active",
            width: 80,
            render: (active) => (
                <Badge
                    status={active ? "processing" : "default"}
                    text={active ? "Sí" : "No"}
                />
            ),
        },
        {
            title: "Fecha Inicio",
            dataIndex: "Starting_Date",
            key: "Starting_Date",
            width: 120,
            render: (date) => (
                <Space>
                    <CalendarOutlined />
                    {formatDate(date)}
                </Space>
            ),
        },
        {
            title: "Fecha Fin",
            dataIndex: "Ending_Date",
            key: "Ending_Date",
            width: 120,
            render: (date) => (
                <Space>
                    <CalendarOutlined />
                    {formatDate(date)}
                </Space>
            ),
        },
        {
            title: "% Descuento",
            dataIndex: "Discount_Percentage",
            key: "Discount_Percentage",
            width: 120,
            render: (percentage) => (
                percentage > 0 ? (
                    <Space>
                        <PercentageOutlined style={{ color: "#52c41a" }} />
                        {percentage}%
                    </Space>
                ) : "-"
            ),
        },
        {
            title: "Cantidad Descuento",
            dataIndex: "Discount_Amount",
            key: "Discount_Amount",
            width: 150,
            render: (amount) => (
                amount > 0 ? (
                    <Space>
                        <DollarOutlined style={{ color: "#52c41a" }} />
                        {formatAmount(amount)}
                    </Space>
                ) : "-"
            ),
        },
        {
            title: "Total Descontado",
            dataIndex: "Total_Discounted_Amount",
            key: "Total_Discounted_Amount",
            width: 150,
            render: (amount) => (
                <Space>
                    <DollarOutlined style={{ color: "#1890ff" }} />
                    {formatAmount(amount)}
                </Space>
            ),
        },
    ];

    // Calcular estadísticas
    const stats = {
        total: couponsData.length,
        active: couponsData.filter(c => c.Active).length,
        pending: couponsData.filter(c => c.Status === "Pendiente").length,
        used: couponsData.filter(c => c.Status === "Utilizado").length,
    };

    if (loading && couponsData.length === 0) {
        return (
            <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" />
                <p style={{ marginTop: "16px" }}>Cargando cupones activos...</p>
            </div>
        );
    }

    return (
        <div className={isMobile ? "coupons-mobile" : "coupons-container"}>
            <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: "24px" }}>
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            Cupones Activos
                        </Title>
                    </Col>
                    <Col>
                        <Space>
                            <Tooltip title="Actualizar desde servidor">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={forceRefresh}
                                    loading={loading}
                                >
                                    Actualizar
                                </Button>
                            </Tooltip>
                        </Space>
                    </Col>
                </Row>

                {/* Estadísticas */}
                <Row gutter={16} style={{ marginBottom: "24px" }}>
                    <Col xs={12} sm={6}>
                        <Statistic title="Total Cupones" value={stats.total} />
                    </Col>
                    <Col xs={12} sm={6}>
                        <Statistic title="Activos" value={stats.active} valueStyle={{ color: '#3f8600' }} />
                    </Col>
                    <Col xs={12} sm={6}>
                        <Statistic title="Pendientes" value={stats.pending} valueStyle={{ color: '#cf1322' }} />
                    </Col>
                    <Col xs={12} sm={6}>
                        <Statistic title="Utilizados" value={stats.used} valueStyle={{ color: '#1890ff' }} />
                    </Col>
                </Row>

                <Divider />

                {/* Filtros */}
                <Row gutter={16} style={{ marginBottom: "24px" }}>
                    <Col xs={24} sm={12} md={8}>
                        <Search
                            placeholder="Buscar cupones..."
                            allowClear
                            enterButton={<SearchOutlined />}
                            size="middle"
                            onSearch={handleSearch}
                            onChange={(e) => {
                                if (!e.target.value) {
                                    handleSearch("");
                                }
                            }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Select
                            placeholder="Filtrar por estado"
                            style={{ width: "100%" }}
                            value={statusFilter}
                            onChange={handleStatusFilter}
                        >
                            <Option value="all">Todos los estados</Option>
                            <Option value="Pendiente">Pendiente</Option>
                            <Option value="Utilizado">Utilizado</Option>
                            <Option value="Caducado">Caducado</Option>
                            <Option value="Cancelado">Cancelado</Option>
                        </Select>
                    </Col>
                </Row>

                {/* Tabla */}
                <Table
                    className="coupons-table"
                    columns={columns}
                    dataSource={couponsData}
                    rowKey="No"
                    expandable={{
                        expandedRowRender,
                        expandedRowKeys,
                        onExpandedRowsChange: setExpandedRowKeys,
                        expandRowByClick: false,
                        showExpandColumn: false,
                    }}
                    pagination={{
                        total: couponsData.length,
                        pageSize: 50,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} de ${total} cupones`,
                    }}
                    scroll={{ x: 1300, y: isMobile ? 400 : 600 }}
                    locale={{
                        emptyText: (
                            <Empty
                                description="No hay cupones disponibles"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ),
                    }}
                    size={isMobile ? "small" : "middle"}
                    loading={loading}
                />

            </Card>
        </div>
    );
};

export default CouponsList; 